import React, { useState, useEffect } from "react";
import "../styles/AboutPage.scss";
import DOMPurify from "dompurify";
import { useDoctor, useAbout } from "../Apis/useApiQueries";
import {
  FaAward,
  FaUserMd,
  FaHeart,
  FaCheckCircle,
  FaSyringe,
  FaSmile,
  FaSeedling,
  FaStar,
  FaTooth,
} from "react-icons/fa";
import { metaPixelEvents } from "../utils/metaPixel";

const AboutPage = () => {
  const { data: doctor, error: doctorError } = useDoctor();
  const { data: about, error: aboutError } = useAbout();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    // Track about page view
    metaPixelEvents.trackAboutView();
  }, []);

  // Show error if any request fails
  if (doctorError || aboutError) {
    return (
      <div className="error-container">
        <p className="error-message">
          {doctorError?.message || aboutError?.message}
        </p>
      </div>
    );
  }

  const highlights = [
    {
      icon: <FaUserMd />,
      title: "ניסיון מקצועי רב",
      description:
        "מומחיות מוכחת בתחום הרפואה האסתטית והקוסמטית עם התמחות בטיפולים מתקדמים",
    },
    {
      icon: <FaAward />,
      title: "הכשרה מתקדמת",
      description:
        "הכשרה מתמשכת ועדכון בטכנולוגיות החדישות ביותר בתחום הרפואה האסתטית",
    },
    {
      icon: <FaHeart />,
      title: "טיפול אישי ומסור",
      description:
        "גישה אישית, חמה ומקצועית לכל מטופל עם הקשבה לצרכים ולציפיות האישיות",
    },
    {
      icon: <FaCheckCircle />,
      title: "תוצאות מוכחות ומרשימות",
      description:
        "מאות מטופלים מרוצים, תוצאות מוכחות ועדויות חיוביות על איכות הטיפול",
    },
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className={`about-hero ${isVisible ? "visible" : ""}`}>
        <div className="hero-decorative-elements">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
        </div>
        <div className="hero-content">
          <div className="hero-text-side">
            <h1 className="main-title">
              <span className="title-line">פגוש את</span>
              <span className="title-highlight">ד"ר שי תלם</span>
            </h1>
            {doctor && (
              <div className="doctor-info">
                <div className="info-content">
                  <h3 className="info-title">רופא מומחה</h3>
                  <p className="doctor-specialty">{doctor.specialty}</p>

                  <div className="specialty-list">
                    <div className="specialty-item">טיפולי בוטוקס</div>
                    <div className="specialty-item">יישור שיניים</div>
                    <div className="specialty-item">הלבנת שיניים</div>
                    <div className="specialty-item">פיסול פנים</div>
                    <div className="specialty-item">פיסול שפתיים</div>
                    <div className="specialty-item">פיסול אף</div>
                    <div className="specialty-item">טיפולי שיער</div>
                  </div>

                  <div className="credentials-simple">
                    <div className="credential">
                      <span className="label">הכשרה</span>
                      <span className="value">אוניברסיטת תל אביב</span>
                    </div>
                    <div className="credential">
                      <span className="label">מיקומים</span>
                      <span className="value">2 מרפאות</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">★★★★★</span>
                <span className="stat-label">דירוג מטופלים</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100%</span>
                <span className="stat-label">שביעות רצון</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">מוסמך</span>
                <span className="stat-label">רופא מומחה</span>
              </div>
            </div>
          </div>
          <div className="hero-image-side">
            <div className="doctor-image-container">
              <div className="image-frame">
                <div className="doctor-image"></div>
              </div>
              <div className="image-decorations">
                <div className="decoration-dot dot-1"></div>
                <div className="decoration-dot dot-2"></div>
                <div className="decoration-dot dot-3"></div>
                <div className="decoration-line line-1"></div>
                <div className="decoration-line line-2"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="about-content">
        <div className="content-container">
          {/* Introduction Section */}
          <div className="intro-section">
            <div className="section-header">
              <h3>ברוכים הבאים למרפאתנו</h3>
              <div className="header-line"></div>
            </div>
            <div className="intro-content">
              <p className="intro-text">
                במרפאתנו אנו מתמחים ברפואה אסתטית מתקדמת ומעניקים פתרונות
                מותאמים אישית לכל מטופל. אנו מאמינים שיופי אמיתי נובע מהרגשה
                טובה עם עצמנו, ולכן אנו משקיעים בטכנולוגיות המתקדמות ביותר
                ובגישה אישית ומקצועית לכל טיפול.
              </p>
            </div>
          </div>

          {/* Story Section */}
          <div className="story-section">
            <div className="section-header">
              <h3>הסיפור שלנו</h3>
              <div className="header-line"></div>
            </div>
            <div className="story-content">
              <div className="story-text">
                <div className="story-paragraph">
                  <p>
                    ד"ר שי תלם הוא רופא שיניים המתמחה באסתטיקה רפואית, בעל
                    קליניקות בהרצליה ובבאר שבע.
                    <br />
                    הוא דוגל באמינות, כנות והשגת תוצאות טבעיות המותאמות אישית
                    למטופלים מכל המינים ובכל הסגנונות.
                  </p>
                </div>

                <div className="story-paragraph">
                  <p>
                    ד"ר תלם סיים את לימודיו באוניברסיטת תל אביב בבית הספר לרפואת
                    שיניים ועבר הכשרות מתקדמות באסתטיקה ב-Roses Clinic.
                    <br />
                    כמו כן, השתלם אצל החברות המובילות בחומרים החדישים ביותר.
                  </p>
                </div>

                <div className="specialties-header">
                  <h4>תחומי ההתמחות של ד"ר תלם</h4>
                  <div className="specialties-divider"></div>
                </div>

                <div className="specialties-grid">
                  <div className="specialty-card">
                    <div className="specialty-icon">
                      <FaSyringe />
                    </div>
                    <h5>טיפולי חומצה היאלרונית</h5>
                    <p>
                      פיסול אף ללא ניתוח, פיסול שפתיים בשיטת הפפיון, פיסול עצם
                      הלחי, קווי לסת וסנטר, ומילוי קמטים.
                    </p>
                  </div>

                  <div className="specialty-card">
                    <div className="specialty-icon">
                      <FaSmile />
                    </div>
                    <h5>טיפולים בבוטוקס</h5>
                    <p>להחלקת ומניעת קמטי הבעה.</p>
                  </div>

                  <div className="specialty-card">
                    <div className="specialty-icon">
                      <FaSeedling />
                    </div>
                    <h5>טיפולי שיער לגברים ונשים</h5>
                    <p>
                      נוגדי התקרחות ושיפור דלילות תוך שימוש ב-PRP, מזותרפיה,
                      הזרקת תאי גזע והחדרת תרופות נוגדות קרחת לקרקפת.
                    </p>
                  </div>

                  <div className="specialty-card">
                    <div className="specialty-icon">
                      <FaStar />
                    </div>
                    <h5>טיפולים לאיכות העור</h5>
                    <p>
                      בפנים ובצוואר דוגמת ביוסטימולטורים, מזותרפיה, טיפול "ערפד"
                      ועוד.
                    </p>
                  </div>

                  <div className="specialty-card">
                    <div className="specialty-icon">
                      <FaTooth />
                    </div>
                    <h5>שירותים נוספים</h5>
                    <p>הלבנת שיניים, יישור שיניים שקוף, וציפויי שיניים.</p>
                  </div>
                </div>

                <div className="story-paragraph">
                  <p>
                    ד"ר תלם מלמד את תחום האסתטיקה לרופאים צעירים ומשמש כמדריך
                    ומרצה באוניברסיטת תל אביב ובמסגרות נוספות אצל החברות
                    המובילות בתחום.
                    <br />
                    כל זאת תוך שימוש בחומרים ובטכנולוגיות המתקדמות ביותר.
                  </p>
                </div>

                <div className="story-paragraph">
                  <p>
                    ד"ר תלם משתתף באופן קבוע בכנסים בינלאומיים ומתעדכן בטכניקות
                    ובחידושים האחרונים בתחום הרפואה האסתטית,
                    <br />
                    במטרה להעניק למטופליו את הטיפולים המתקדמים והבטוחים ביותר.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Highlights Section */}
          <div className="highlights-section">
            <div className="section-header">
              <h3>מה מייחד אותנו</h3>
              <div className="header-line"></div>
            </div>
            <div className="highlights-grid">
              {highlights.map((highlight, index) => (
                <div
                  key={index}
                  className={`highlight-card ${isVisible ? "animate" : ""}`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="highlight-icon">{highlight.icon}</div>
                  <h4 className="highlight-title">{highlight.title}</h4>
                  <p className="highlight-description">
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
